import { useRef, useState, useEffect, useCallback } from 'react'
import { useLanguage } from '../context/LangaugeContextProvider/LangaugeContextProvider'
import { isArabic } from '../utils'

const useHorizontalScroll = (scrollValue = 180, avoidRtl) => {
  const scrollRef = useRef(null)
  const [isAtStart, setIsAtStart] = useState(true)
  const [isAtEnd, setIsAtEnd] = useState(false)
  const { language } = useLanguage()
  const isArabicPage = isArabic(language?.code)

  const checkScrollPosition = useCallback(() => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current
      if (isArabicPage && !avoidRtl) {
        //scrollLeft value is negative in rtl view
        setIsAtStart(scrollLeft >= 0)
        setIsAtEnd(Math.abs(scrollLeft) + clientWidth >= scrollWidth - 2)
      } else {
        setIsAtStart(scrollLeft === 0)
        setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 2)
      }
    }
  }, [])

  const smoothScroll = (distance) => {
    const startTime = performance.now()
    const duration = 500 // duration in milliseconds
    const startScrollLeft = scrollRef.current.scrollLeft

    const animate = (currentTime) => {
      const elapsedTime = currentTime - startTime
      const progress = Math.min(elapsedTime / duration, 1)
      scrollRef.current.scrollLeft = startScrollLeft + distance * progress

      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        checkScrollPosition()
      }
    }

    requestAnimationFrame(animate)
  }

  const scrollLeft = () => {
    if (scrollRef.current) {
      smoothScroll(-scrollValue) // Adjust the value as needed
    }
  }

  const scrollRight = () => {
    if (scrollRef.current) {
      smoothScroll(scrollValue) // Adjust the value as needed
    }
  }

  useEffect(() => {
    const currentRef = scrollRef.current
    if (currentRef) {
      currentRef.addEventListener('scroll', checkScrollPosition)
      checkScrollPosition() // Check position on mount

      const resizeObserver = new ResizeObserver(() => {
        checkScrollPosition()
      })

      // Observe the scroll container
      resizeObserver.observe(currentRef)

      // Observe all child elements of the scroll container
      const observeChildren = () => {
        Array.from(currentRef.children).forEach((child) => {
          resizeObserver.observe(child)
        })
      }

      // Initial observation of children
      observeChildren()

      // MutationObserver to detect changes in the DOM structure
      const mutationObserver = new MutationObserver((mutationsList) => {
        mutationsList.forEach((mutation) => {
          if (mutation.type === 'childList') {
            observeChildren()
            checkScrollPosition()
          }
        })
      })

      mutationObserver.observe(currentRef, { childList: true })

      return () => {
        currentRef.removeEventListener('scroll', checkScrollPosition)
        resizeObserver.disconnect()
        mutationObserver.disconnect()
      }
    }
  }, [checkScrollPosition])

  return { scrollRef, scrollLeft, scrollRight, isAtStart, isAtEnd }
}

export default useHorizontalScroll
